import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Download, ArrowUpDown, Image } from "lucide-react";
import { NFTAnalytics } from "@/hooks/useAnalytics";
import { exportToCSV, exportToJSON, shortenWallet } from "@/lib/exportUtils";
import { priceToUsd, formatUsdPrice } from "@/lib/priceUtils";
import { useSolPrice } from "@/hooks/useSolPrice";

interface NFTAnalyticsTableProps {
  data: NFTAnalytics[];
  title?: string;
  showExport?: boolean;
}

type SortKey = "name" | "sales_count" | "total_earned" | "display_price" | "created_at";
type SortOrder = "asc" | "desc";

export function NFTAnalyticsTable({
  data,
  title = "NFT Statistics",
  showExport = true,
}: NFTAnalyticsTableProps) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("total_earned");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const { usdToSol } = useSolPrice();

  // Format price: USD primary, SOL secondary
  const formatPriceDisplay = (value: number) => {
    const usdValue = priceToUsd(value);
    const solValue = usdToSol(usdValue);
    return {
      usd: formatUsdPrice(usdValue),
      sol: `≈ ${solValue.toFixed(4)} SOL`
    };
  };

  const filteredAndSorted = useMemo(() => {
    let filtered = data.filter(
      (nft) =>
        nft.name.toLowerCase().includes(search.toLowerCase()) ||
        nft.owner_wallet.toLowerCase().includes(search.toLowerCase()) ||
        (nft.owner_name?.toLowerCase() || "").includes(search.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aVal: any = a[sortKey];
      let bVal: any = b[sortKey];

      if (sortKey === "created_at") {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }

      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = (bVal || "").toLowerCase();
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data, search, sortKey, sortOrder]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortOrder("desc");
    }
  };

  const handleExport = (format: "csv" | "json") => {
    const exportData = filteredAndSorted.map((n) => ({
      name: n.name,
      owner_wallet: n.owner_wallet,
      owner_name: n.owner_name || "",
      price_usd: priceToUsd(n.display_price),
      sales_count: n.sales_count,
      total_earned_usd: priceToUsd(n.total_earned),
      avg_sale_price_usd: priceToUsd(n.avg_sale_price),
      is_listed: n.is_listed ? "Yes" : "No",
      created_at: n.created_at || "",
    }));

    if (format === "csv") {
      exportToCSV(exportData, "nft-analytics");
    } else {
      exportToJSON(exportData, "nft-analytics");
    }
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="text-lg">{title}</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search NFTs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {showExport && (
              <Select onValueChange={(v) => handleExport(v as "csv" | "json")}>
                <SelectTrigger className="w-auto">
                  <Download className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Export" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">Export CSV</SelectItem>
                  <SelectItem value="json">Export JSON</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>NFT</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort("display_price")}
                    className="font-medium"
                  >
                    Price
                    <ArrowUpDown className="ml-1 w-3 h-3" />
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort("sales_count")}
                    className="font-medium"
                  >
                    Sales
                    <ArrowUpDown className="ml-1 w-3 h-3" />
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort("total_earned")}
                    className="font-medium"
                  >
                    Total Earned
                    <ArrowUpDown className="ml-1 w-3 h-3" />
                  </Button>
                </TableHead>
                <TableHead>Avg Sale</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No NFTs found
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSorted.map((nft) => {
                  const priceDisplay = formatPriceDisplay(nft.display_price);
                  const earnedDisplay = formatPriceDisplay(nft.total_earned);
                  const avgDisplay = formatPriceDisplay(nft.avg_sale_price);
                  
                  return (
                    <TableRow key={nft.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                            <Image className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <span className="font-medium truncate max-w-[120px]">
                            {nft.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {nft.owner_name || shortenWallet(nft.owner_wallet)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{priceDisplay.usd}</span>
                          <span className="text-xs text-muted-foreground">{priceDisplay.sol}</span>
                        </div>
                      </TableCell>
                      <TableCell>{nft.sales_count}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-green-500">{earnedDisplay.usd}</span>
                          <span className="text-xs text-muted-foreground">{earnedDisplay.sol}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-muted-foreground">{avgDisplay.usd}</span>
                          <span className="text-xs text-muted-foreground">{avgDisplay.sol}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Badge variant={nft.is_listed ? "default" : "secondary"}>
                            {nft.is_listed ? "Listed" : "Unlisted"}
                          </Badge>
                          {nft.image_verified && (
                            <Badge variant="outline" className="text-green-500 border-green-500">
                              Verified
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4 text-xs text-muted-foreground">
          Showing {filteredAndSorted.length} of {data.length} NFTs
        </div>
      </CardContent>
    </Card>
  );
}
